/**
 * email-helpers.js — re-exports from @tshmanager/email-shared + web-specific wrappers.
 */
import {
  tagTextColor,
  humanFileSize,
  formatRelativeTime,
  formatShortDate,
  formatLongDate,
  formatAddressDisplay,
  fileKindToBootstrapIcon,
  getFileKind,
  emailDateGroup as sharedEmailDateGroup,
  groupEmailsByDate as sharedGroupEmailsByDate,
} from '@tshmanager/email-shared'

export {
  tagTextColor,
  humanFileSize,
  formatAddressDisplay,
  fileKindToBootstrapIcon,
  getFileKind,
}

/** @deprecated use formatAddressDisplay from email-shared */
export function emailAddressFormat(addrObj) {
  if (!addrObj) return ''
  // Guard: callers sometimes nest a full AddressEntry under `address`
  // ({ address: { name, email }, name }) — unwrap so we never return an object.
  if (addrObj && typeof addrObj === 'object' && addrObj.address && typeof addrObj.address === 'object') {
    const nested = addrObj.address
    return formatAddressDisplay({
      ...nested,
      name: addrObj.name || nested.name,
    }) || ''
  }
  const formatted = formatAddressDisplay(addrObj)
  return typeof formatted === 'string' ? formatted : ''
}

/** Bootstrap file icon class for a filename. */
export function fileIconClass(filename) {
  return fileKindToBootstrapIcon(getFileKind(filename ?? ''))
}

const HU_RELATIVE_LABELS = {
  justNow:   'most',
  oneMinute: '1 perce',
  minutes:   (n) => `${n} perce`,
  oneHour:   '1 órája',
  hours:     (n) => `${n} órája`,
  yesterday: 'tegnap',
  days:      (n) => `${n} napja`,
  oneWeek:   '1 hete',
  weeks:     (n) => `${n} hete`,
  oneMonth:  '1 hónapja',
  months:    (n) => `${n} hónapja`,
  years:     (n) => `${n} éve`,
}

function documentLocale() {
  if (typeof document !== 'undefined') return document.documentElement.lang || 'hu'
  return 'hu'
}

/** Human-readable relative time (Hungarian labels, web email UI). */
export function timeAgo(isoString) {
  return formatRelativeTime(isoString, HU_RELATIVE_LABELS, documentLocale())
}

/** Short date format — mirrors Rails l(time, format: :short). */
export function formatDate(isoString, includeYear = false) {
  return formatShortDate(isoString, documentLocale(), includeYear)
}

/** Long date format — mirrors Rails l(time, format: :long). */
export function formatDateLong(isoString) {
  return formatLongDate(isoString, documentLocale())
}

/**
 * Date-bucket key for list grouping — mirrors EmailsHelper#email_date_group.
 * Re-exported from @tshmanager/email-shared (single source of truth, shared
 * with the mobile app).
 * @returns {'today'|'yesterday'|'this_week'|'last_week'|'this_month'|'older'}
 */
export const emailDateGroup = sharedEmailDateGroup

/**
 * Group a list of emails by date bucket (see `emailDateGroup`), returning
 * `[{ key, emails }]` in stable newest-to-oldest order. Re-exported from
 * @tshmanager/email-shared.
 */
export function groupEmailsByDate(emails, getTimestamp = (e) => e.last_activity_at || e.last_activity || e.sent_at || e.created_at) {
  return sharedGroupEmailsByDate(emails, getTimestamp).map(({ group, items }) => ({ key: group, emails: items }))
}

/** Display name from canonical Thread.from_address object (or legacy string). */
export function threadFromDisplayName(thread) {
  const addr = thread?.from_address
  if (addr && typeof addr === 'object') {
    return addr.name || addr.email || addr.address || thread.from_name || ''
  }
  if (typeof addr === 'string' && addr) return addr
  return thread?.from_name || ''
}

/** Applied tag ids from Thread.tags (or legacy email_tag_ids). */
export function threadTagIds(thread) {
  if (Array.isArray(thread?.tags) && thread.tags.length) {
    return thread.tags.map(t => Number(t.id))
  }
  return (thread?.email_tag_ids || []).map(Number)
}

/**
 * Resolve read/starred/archived/trashed/spam for toolbar buttons.
 * Prefer list row (optimistic), then thread show payload.
 */
export function threadUiState(thread, emailRow) {
  const row = emailRow || {}
  const t = thread || {}
  return {
    read: row.read ?? t.read ?? !!t.user_state?.read_at,
    starred: !!(row.starred ?? t.starred ?? t.user_state?.starred ?? t.user_state?.starred_at),
    archived: !!(row.archived || row.archived_at || t.archived || t.archived_at || t.user_state?.archived_at),
    trashed: !!(row.trashed || row.trashed_at || t.trashed || t.trashed_at || t.user_state?.trashed_at),
    spam: !!(row.spam || row.spam_at || t.spam || t.spam_at),
    folderId: row.folder_id ?? t.folder_id ?? t.email_folder_id ?? null,
  }
}
